import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { concreteLoanFormatNameToKey } from '../../../store/loanFormat/actions';
import Utils from '../../../shared/utils';
import ProductCover from '../../../shared/common/ProductCover';
import ProductLanguage from '../../../shared/product/ProductLanguage';
import { PreviewButton } from '../../../shared/preview/PreviewButton';
import ProductSeries from '../../../shared/product/ProductSeries';
import ProductAuthors from '../../../shared/product/ProductAuthors';
import ProductNarrators from '../../../shared/product/ProductNarrators';
import ProductGenres from '../../../shared/product/ProductGenres';
import ProductAge from '../../../shared/product/ProductAge';
import { ProductReleaseDate } from '../../../shared/product/ProductReleaseDate';
import ProductPublisher from '../../../shared/product/ProductPublisher';
import { ProductFormat } from '../../../shared/product/ProductFormat';
import ProductUsageConditions from '../../../shared/product/ProductUsageConditions';
import { TileBadges } from '../../../shared/common/BadgesV2';
import ProductStatistics from '../../../shared/product/ProductStatistics';
import SelectButton from '../../../shared/common/SelectButton';
import LibraryManagedProductGroupDropdown from '../../../shared/common/LibraryManagedProductGroupDropdown';
import { DisableWrapper } from '../../../shared/common/Wrapper/DisableWrapper';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { MATERIAL_ICON_SETTINGS, MATERIAL_ICON_TRASH } from '../../../shared/common/Button/Icon';
import { useTranslation } from 'react-i18next';
import { AddProduct } from '../../../shared/common/AddProduct/AddProduct';
import { LibraryManagedProductGroupOnSiteOut } from '../../../store/api-catman';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ProductMetaData } from '../../../../models/Product';
import { Article } from '../../../../models/Article';
import { ProductSelection } from '../../../shared/product-selection/ProductSelectionTypes';

const StSmallSpacer = styled.div`
    height: 5px;
    width: 100%;
`;

const StLibraryManagedProductGroupDropdown = styled(LibraryManagedProductGroupDropdown)`
    margin-top: 5px;
`;

interface Props {
    product?: any;
    showRemoveButton?: boolean;
    showSelectButton?: boolean;
    minimalView?: boolean;
    onProductImageClick?: () => void;
    onRemoveProductFromList?: (product: ProductMetaData & Article) => void;
    onSelectProduct?: (id: string) => void;
    productGroups: LibraryManagedProductGroupOnSiteOut[];
    showManagePopup?: (product: any) => void;
    onAddToGroup: (productGroupId: number, product: ProductMetaData & Article) => void;
    productSelection: ProductSelection;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    isProductSelectable: boolean;
}

export const ProductGroupTile = ({
    product = {},
    minimalView = false,
    showRemoveButton = false,
    showSelectButton = false,
    onSelectProduct = () => {},
    onRemoveProductFromList = () => {},
    showManagePopup,
    productGroups,
    onAddToGroup,
    onProductImageClick,
    productSelection,
    onAddPrice,
    isProductSelectable
}: Props) => {
    const { t } = useTranslation();
    const prdTileInnerRef = useRef(null);
    const [showDetails, setShowDetails] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const showSelectedPriceOnly = false;
    const allowPriceSelection = true;

    useEffect(() => {
        setIsDisabled((product.copyStockAmount ?? 0) <= 0);
    }, [product]);

    if (!product) {
        return (
            <div>
                <p>{t('marketing.productGroups.warnings.noProducts')}</p>
            </div>
        );
    }

    const isRemoveButtonShown = (showRemoveButton && minimalView && showDetails) || (showRemoveButton && !minimalView);
    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const shortenedTitle = Utils.truncate(product.title);
    const loanFormatClass = classNames('prd-tile', 'cc-prd-tile', { minimal: minimalView });
    const productLink = `/products/${product.id}`;
    const tileClasses = classNames('prd-tile-inner', { over: showDetails });

    return (
        <div>
            <div className={loanFormatClass} onMouseLeave={() => setShowDetails(false)}>
                <div ref={prdTileInnerRef} className={tileClasses}>
                    <div
                        className="packshot"
                        data-displayname="CollectionProductTile"
                        data-testid="CollectionProductTile"
                    >
                        <DisableWrapper isDisabled={isDisabled}>
                            {
                                <Link
                                    to={productLink}
                                    onClick={e => {
                                        if (onProductImageClick) {
                                            e.preventDefault();
                                            onProductImageClick();
                                        }
                                    }}
                                >
                                    <ProductCover
                                        onMouseEnter={() => setShowDetails(true)}
                                        minSizeInPx={300}
                                        product={product}
                                    />
                                </Link>
                            }
                        </DisableWrapper>
                        {showSelectButton ? (
                            <SelectButton
                                isProductSelectable={isProductSelectable}
                                productOrId={product.id}
                                productSelection={productSelection}
                                onClick={() => onSelectProduct(product.id)}
                            />
                        ) : null}
                        <PreviewButton product={product} />
                    </div>

                    <DisableWrapper isDisabled={isDisabled}>
                        <div className="details" data-displayname="CollectionProductTileDetails">
                            <ProductSeries
                                product={product}
                                onMouseEnter={() => setShowDetails(true)}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                            <h3 onMouseEnter={() => setShowDetails(true)}>
                                <Link to={productLink}>{shortenedTitle}</Link>
                            </h3>
                            <ProductAuthors
                                product={product}
                                onMouseEnter={() => setShowDetails(true)}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                            <div className="details-inner expanded">
                                <ProductNarrators
                                    product={product}
                                    loanFormatKey={productLoanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductGenres
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                                <ProductLanguage
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    label={product.language}
                                    shopLanguage={undefined}
                                />
                                <ProductReleaseDate product={product} />
                                <ProductPublisher
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductFormat
                                    product={product}
                                    loanFormatKey={productLoanFormatKey}
                                    shopLanguage={undefined}
                                />
                                <ProductUsageConditions
                                    product={product}
                                    loanFormatKey={loanFormatKey}
                                    shopLanguage={undefined}
                                />
                            </div>
                        </div>
                    </DisableWrapper>
                    <div className="prd-bottom" data-displayname="CollectionProductTileBottom">
                        <DisableWrapper isDisabled={isDisabled}>
                            <TileBadges
                                product={product}
                                loanFormatKey={loanFormatKey}
                                showAll={showDetails}
                                shopLanguage={undefined}
                            />
                        </DisableWrapper>
                        {!minimalView || showDetails ? (
                            <DisableWrapper isDisabled={isDisabled}>
                                <ProductStatistics
                                    product={product}
                                    loanFormatKey={productLoanFormatKey}
                                    isLibraryManagedProduct={true}
                                />
                            </DisableWrapper>
                        ) : null}
                        <div>
                            {isRemoveButtonShown ? (
                                <Button
                                    text={t('remove')}
                                    onClick={() => onRemoveProductFromList(product)}
                                    icon={MATERIAL_ICON_TRASH}
                                    size={ButtonSize.SMALL}
                                    theme={ButtonTheme.NEUTRAL}
                                    fullWidth={true}
                                    data-displayname="RemoveButton"
                                />
                            ) : null}

                            {showDetails ? (
                                <>
                                    {showManagePopup ? (
                                        <>
                                            <StSmallSpacer />
                                            <Button
                                                text={t('manage')}
                                                onClick={() => showManagePopup(product)}
                                                icon={MATERIAL_ICON_SETTINGS}
                                                size={ButtonSize.SMALL}
                                                theme={ButtonTheme.NEUTRAL}
                                                fullWidth={true}
                                            />
                                        </>
                                    ) : null}

                                    <StLibraryManagedProductGroupDropdown
                                        productGroups={productGroups}
                                        onListClick={productGroupId => onAddToGroup(productGroupId, product)}
                                    />
                                    <StSmallSpacer />

                                    <AddProduct
                                        product={product}
                                        onAddPrice={onAddPrice}
                                        onSelectPrice={async () => {}}
                                        showSelectedPriceOnly={showSelectedPriceOnly}
                                        allowSelection={allowPriceSelection}
                                        onHideDetails={() => setShowDetails(false)}
                                    />
                                </>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
